import { Box, Card, CardContent, CardHeader, LinearProgress, Typography, Alert } from '@mui/material';
import { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import {
	BooleanInput,
	Edit,
	FormTab,
	ImageField,
	ImageInput,
	NumberInput,
	ReferenceArrayInput,
	SelectArrayInput,
	TabbedForm,
	TextField,
	TextInput,
	useEditController,
	useNotify,
} from 'react-admin';
import { Personalization } from '../Meeting/MeetingCreate';
import SelectMeetingByDate from '../SelectMeetingByDate';
import constants from '../../constants';
import axios, { AxiosError } from 'axios';

const VideoUploadComponent = ({ vodId }: { vodId: string }) => {
	const [uploading, setUploading] = useState(false);
	const [progress, setProgress] = useState(0);
	const [error, setError] = useState<string | null>(null);
	const [videoUrl, setVideoUrl] = useState<string | null>(null);
	const notify = useNotify();
	const { setValue } = useFormContext();

	const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0];
		if (!file) return;

		// Validation
		if (!file.type.startsWith('video/')) {
			setError('רק קבצי וידאו מותרים');
			return;
		}

		// Check for spaces in filename
		if (file.name.includes(' ')) {
			setError('שם הקובץ לא יכול להכיל רווחים');
			return;
		}

		// Check for Hebrew characters in filename
		const hebrewPattern = /[\u0590-\u05FF]/;
		if (hebrewPattern.test(file.name)) {
			setError('שם הקובץ לא יכול להכיל תווים עבריים');
			return;
		}

		if (file.size > 400 * 1024 * 1024) {
			setError('הקובץ גדול מדי (מקסימום 400MB)');
			return;
		}

		setError(null);
		setUploading(true);
		setProgress(0);

		try {
			const token = localStorage.getItem('token');
			if (!token) {
				setError('לא נמצא טוקן אימות');
				setUploading(false);
				return;
			}
			const authHeaders = { Authorization: `Bearer ${token}` };

			// GH-691: the file goes straight from the browser to S3 (presigned POST) — never through our
			// server. 1) ask the server for a signed form, 2) POST the file to S3 with real progress,
			// 3) tell the server the key so it can verify the object and attach it to the video.
			const { data: signed } = await axios.post(
				`${constants.API_URL}vod/${vodId}/upload-url`,
				{ filename: file.name, contentType: file.type },
				{ headers: authHeaders },
			);

			await new Promise<void>((resolve, reject) => {
				const formData = new FormData();
				Object.entries(signed.fields as Record<string, string>).forEach(([k, v]) => formData.append(k, v));
				formData.append('file', file); // must be the last field for S3 POST
				const xhr = new XMLHttpRequest();
				xhr.upload.addEventListener('progress', (e) => {
					if (e.lengthComputable) setProgress((e.loaded / e.total) * 100);
				});
				xhr.addEventListener('load', () =>
					xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`S3 upload failed (${xhr.status})`)),
				);
				xhr.addEventListener('error', () => reject(new Error('שגיאת רשת בהעלאת הסרטון')));
				xhr.open('POST', signed.url);
				xhr.send(formData);
			});

			const { data } = await axios.post(
				`${constants.API_URL}vod/${vodId}/upload-complete`,
				{ key: signed.key },
				{ headers: authHeaders },
			);
			setVideoUrl(data.videoUrl);
			setValue('videoUrl', data.videoUrl);
			setUploading(false);
			notify('הסרטון הועלה בהצלחה', { type: 'success' });
		} catch (err: any) {
			const msg = err?.response?.data?.message || err?.message || 'שגיאה בהעלאת הסרטון';
			setError(msg);
			setUploading(false);
			notify(msg, { type: 'error' });
		}
	};

	return (
		<Box sx={{ mb: 2 }}>
			<Typography variant="body2" sx={{ mb: 1, direction: 'rtl' }}>
				העלאת סרטון וידאו
			</Typography>
			<input
				type="file"
				accept="video/*"
				onChange={handleFileChange}
				disabled={uploading}
				style={{ width: '100%', direction: 'rtl' }}
			/>
			{uploading && (
				<Box sx={{ mt: 2 }}>
					<LinearProgress variant="determinate" value={progress} />
					<Typography variant="body2" sx={{ mt: 1, textAlign: 'right' }}>
						{Math.round(progress)}%
					</Typography>
				</Box>
			)}
			{error && (
				<Alert severity="error" sx={{ mt: 2, direction: 'rtl' }}>
					{error}
				</Alert>
			)}
			{videoUrl && (
				<Alert severity="success" sx={{ mt: 2, direction: 'rtl' }}>
					הסרטון הועלה בהצלחה: {videoUrl}
				</Alert>
			)}
		</Box>
	);
};

const VodEdit = () => {
	const record = useEditController();
	const [ignoreAge, setIgnoreAge] = useState(false);

	const transform = (data: any) => ({
		...data,
		age: ignoreAge ? { to: null, from: null } : data.age,
	});
	const notify = useNotify();
	return (
		<Edit transform={transform} mutationMode="pessimistic" resource={'vod/admin'} redirect={'/vod'}>
			<TabbedForm>
				<FormTab label="פרטים">
					<TextField source="id" mb={5} />
					<TextInput source="title" label="שם" dir="rtl" style={{ direction: 'rtl' }} fullWidth />
					<TextInput source="sub_title" label="תת כותרת" dir="rtl" style={{ direction: 'rtl' }} fullWidth />
					<TextInput source="videoUrl" label="קישור לוידאו" fullWidth dir="ltr" />
					<BooleanInput source="is_global" label="וידאו גלובלי" fullWidth />
					{record.record?._id && <VideoUploadComponent vodId={record.record._id} />}

					<ImageInput source="picture" label="Logo">
						<ImageField source="src" />
					</ImageInput>
					<ReferenceArrayInput reference="vod/categories/all" source="category" filter={{ visible: true }} perPage={500}>
						<SelectArrayInput optionText={'hebrew_name'} />
					</ReferenceArrayInput>
					<Personalization setIgnoreAge={setIgnoreAge} ignoreAge={ignoreAge} />
					<TextInput source="description" label="תיאור" fullWidth multiline />
					<TextInput source="description_temp" label="תיאור שירות" fullWidth multiline />
					<TextInput source="description_temp_en" label="תיאור שירות אנגלית" fullWidth multiline />
					<NumberInput source="priority" label="קדימות" fullWidth />
					<BooleanInput source="visible" label="זמין" fullWidth />
				</FormTab>
				<FormTab label="הקלטה">
					<Box py={2} mx={'auto'}>
						<Card style={{ width: 600 }}>
							<CardHeader title="הקלטה"></CardHeader>
							<CardContent>
								<SelectMeetingByDate
									initValue={record.record?.recorded_meeting}
									onMeetingUpdate={async (id) => {
										try {
											await axios.put(`${constants.API_URL}/meeting/${id}/record`, {
												video_id: record.record._id,
											});
											notify('ההקלטה נשמרה', { type: 'success' });
										} catch (error) {
											notify(`משהו השתבש (${(error as AxiosError).response?.status})`, {
												type: 'error',
											});
										}
									}}
								/>
							</CardContent>
						</Card>
					</Box>
				</FormTab>
			</TabbedForm>
		</Edit>
	);
};
export default VodEdit;
